import { useMemo, useState } from 'react';
import { Plus, RotateCcw, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  cn,
} from '@mlm/ui';
import {
  DEFAULT_PERSONAS,
  normalizeAssistantMemoryKey,
  personaEnabled,
  resolvePersonas,
  type AssistantPersona,
  type AssistantPersonaOverride,
} from '@mlm/shared';

/**
 * Persona editor for Settings → AI Assistant.
 *
 * Edits the tenant's `features.assistant.personas[]` OVERRIDE list, not the
 * personas themselves: for a built-in we store only the fields that actually
 * differ from `DEFAULT_PERSONAS`, so untouched copy keeps tracking future
 * default improvements and "Reset" is just dropping the entry. Unknown ids
 * append a brand-new persona (account tools off unless explicitly granted).
 *
 * Nothing here talks to the server — it mutates the Settings page's draft and
 * rides the existing Save (which is audited as `tenant.features.update`).
 */
export function AssistantPersonaEditor({
  overrides,
  onChange,
  disabled = false,
}: {
  overrides: AssistantPersonaOverride[];
  onChange: (next: AssistantPersonaOverride[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const resolved = useMemo(() => resolvePersonas(overrides), [overrides]);
  const editing = resolved.find((p) => p.id === editingId) ?? null;
  const isBuiltIn = (id: string) => DEFAULT_PERSONAS.some((p) => p.id === id);

  /** Merge a patch into this persona's override entry, dropping no-op fields. */
  function patch(id: string, changes: Partial<AssistantPersona>) {
    const base = DEFAULT_PERSONAS.find((p) => p.id === id);
    const existing = overrides.find((o) => o.id === id) ?? { id };
    const merged: AssistantPersonaOverride = { ...existing, ...changes };
    // Keep only what differs from the built-in default (custom personas have
    // no base, so everything on them is kept).
    const trimmed: AssistantPersonaOverride = { id };
    for (const [key, value] of Object.entries(merged)) {
      if (key === 'id' || value === undefined) continue;
      const baseValue = base?.[key as keyof AssistantPersona];
      if (base && JSON.stringify(baseValue) === JSON.stringify(value)) continue;
      (trimmed as Record<string, unknown>)[key] = value;
    }
    const rest = overrides.filter((o) => o.id !== id);
    // An override that no longer differs from the default is just noise.
    onChange(Object.keys(trimmed).length === 1 && base ? rest : [...rest, trimmed]);
  }

  function resetPersona(id: string) {
    onChange(overrides.filter((o) => o.id !== id));
    if (!isBuiltIn(id)) setEditingId(null);
  }

  function addPersona() {
    const id = `custom-${Date.now().toString(36)}`;
    onChange([
      ...overrides,
      { id, name: 'New assistant', icon: '🤖', tagline: '', instructions: '' },
    ]);
    setEditingId(id);
  }

  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Personas</p>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setOpen(true)}>
          Manage personas
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {resolved.map((p) => (
          <Badge
            key={p.id}
            variant="outline"
            title={p.tagline}
            className={cn(!personaEnabled(p) && 'opacity-50 line-through')}
          >
            {p.icon} {p.name}
            {p.allowAccountTools ? ' · account-aware' : ''}
          </Badge>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Personas</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Edits apply when you save the Settings page. Disabling a persona hides it from the
              picker; members already chatting with it keep their conversations.
            </p>

            <div className="flex flex-wrap gap-1.5">
              {resolved.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setEditingId(p.id === editingId ? null : p.id)}
                  className={cn(
                    'rounded-md border px-2 py-1 text-xs transition-colors',
                    p.id === editingId ? 'border-primary bg-muted' : 'hover:bg-muted/60',
                    !personaEnabled(p) && 'opacity-50',
                  )}
                >
                  {p.icon} {p.name}
                </button>
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                disabled={disabled}
                onClick={addPersona}
              >
                <Plus className="mr-1 h-3 w-3" /> Add persona
              </Button>
            </div>

            {editing ? (
              <PersonaForm
                key={editing.id}
                persona={editing}
                isBuiltIn={isBuiltIn(editing.id)}
                overridden={overrides.some((o) => o.id === editing.id)}
                disabled={disabled}
                onPatch={(changes) => patch(editing.id, changes)}
                onReset={() => resetPersona(editing.id)}
              />
            ) : (
              <p className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
                Pick a persona above to edit it.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PersonaForm({
  persona,
  isBuiltIn,
  overridden,
  disabled,
  onPatch,
  onReset,
}: {
  persona: AssistantPersona;
  isBuiltIn: boolean;
  overridden: boolean;
  disabled: boolean;
  onPatch: (changes: Partial<AssistantPersona>) => void;
  onReset: () => void;
}) {
  const intake = persona.intake ?? [];
  const chips = persona.suggestedQuestions ?? [];

  return (
    <div className="space-y-4 rounded-md border p-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Icon" hint="Emoji only">
          <Input
            className="h-8 w-16 text-center"
            maxLength={8}
            disabled={disabled}
            value={persona.icon}
            onChange={(e) => onPatch({ icon: e.target.value })}
          />
        </Field>
        <Field label="Name" className="min-w-[12rem] flex-1">
          <Input
            className="h-8"
            maxLength={60}
            disabled={disabled}
            value={persona.name}
            onChange={(e) => onPatch({ name: e.target.value })}
          />
        </Field>
        <Field label="Voice hint" hint="e.g. en-PH — read-aloud only">
          <Input
            className="h-8 w-28"
            maxLength={40}
            disabled={disabled}
            value={persona.voiceHint ?? ''}
            onChange={(e) => onPatch({ voiceHint: e.target.value || undefined })}
          />
        </Field>
      </div>

      <Field label="Tagline" hint="One line under the name in the picker">
        <Input
          className="h-8"
          maxLength={160}
          disabled={disabled}
          value={persona.tagline}
          onChange={(e) => onPatch({ tagline: e.target.value })}
        />
      </Field>

      <Field
        label="Instructions"
        hint="How this persona should behave. Written to the model as-is."
      >
        <Textarea
          rows={5}
          maxLength={4000}
          disabled={disabled}
          value={persona.instructions}
          onChange={(e) => onPatch({ instructions: e.target.value })}
        />
      </Field>

      <div className="flex flex-wrap gap-4 text-xs">
        <CheckboxRow
          label="Enabled"
          hint="Off hides it from the picker"
          checked={personaEnabled(persona)}
          disabled={disabled}
          onChange={(v) => onPatch({ enabled: v ? undefined : false })}
        />
        <CheckboxRow
          label="Account-aware"
          hint="May read the member's own wallet, team, orders"
          checked={persona.allowAccountTools}
          disabled={disabled}
          onChange={(v) => onPatch({ allowAccountTools: v })}
        />
        <CheckboxRow
          label="Web search"
          hint="Still requires the tenant-level toggle"
          checked={persona.allowWebSearch}
          disabled={disabled}
          onChange={(v) => onPatch({ allowWebSearch: v })}
        />
      </div>

      {/* Intake — the small profile this persona asks for up front. */}
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Profile questions (asked on the first chat; answers are remembered)
        </p>
        {intake.map((q, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <Input
              className="h-8 w-40"
              placeholder="memory key"
              maxLength={64}
              disabled={disabled}
              value={q.key}
              onChange={(e) =>
                onPatch({
                  intake: intake.map((row, j) =>
                    j === i ? { ...row, key: normalizeAssistantMemoryKey(e.target.value) } : row,
                  ),
                })
              }
            />
            <Input
              className="h-8 min-w-[14rem] flex-1"
              placeholder="Question shown to the member"
              maxLength={200}
              disabled={disabled}
              value={q.question}
              onChange={(e) =>
                onPatch({
                  intake: intake.map((row, j) =>
                    j === i ? { ...row, question: e.target.value } : row,
                  ),
                })
              }
            />
            <Input
              className="h-8 w-40"
              placeholder="placeholder (optional)"
              maxLength={120}
              disabled={disabled}
              value={q.placeholder ?? ''}
              onChange={(e) =>
                onPatch({
                  intake: intake.map((row, j) =>
                    j === i ? { ...row, placeholder: e.target.value || undefined } : row,
                  ),
                })
              }
            />
            <RowDelete
              disabled={disabled}
              label="Remove question"
              onClick={() => onPatch({ intake: intake.filter((_, j) => j !== i) })}
            />
          </div>
        ))}
        {intake.length < 8 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            disabled={disabled}
            onClick={() => onPatch({ intake: [...intake, { key: '', question: '' }] })}
          >
            <Plus className="mr-1 h-3 w-3" /> Add question
          </Button>
        )}
      </div>

      {/* Starter chips — `{key}` pulls the member's answer for that key. */}
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Starter questions (shown on an empty chat; <code>{'{key}'}</code> inserts a profile
          answer, and a chip whose key is unanswered is hidden)
        </p>
        {chips.map((chip, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              className="h-8 flex-1"
              maxLength={200}
              disabled={disabled}
              value={chip}
              onChange={(e) =>
                onPatch({ suggestedQuestions: chips.map((c, j) => (j === i ? e.target.value : c)) })
              }
            />
            <RowDelete
              disabled={disabled}
              label="Remove starter question"
              onClick={() => onPatch({ suggestedQuestions: chips.filter((_, j) => j !== i) })}
            />
          </div>
        ))}
        {chips.length < 8 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            disabled={disabled}
            onClick={() => onPatch({ suggestedQuestions: [...chips, ''] })}
          >
            <Plus className="mr-1 h-3 w-3" /> Add starter question
          </Button>
        )}
      </div>

      {overridden && (
        <div className="border-t pt-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-destructive"
            disabled={disabled}
            onClick={onReset}
          >
            {isBuiltIn ? (
              <>
                <RotateCcw className="mr-1 h-3 w-3" /> Reset to the built-in defaults
              </>
            ) : (
              <>
                <Trash2 className="mr-1 h-3 w-3" /> Delete this persona
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('space-y-1', className)}>
      <label className="text-xs text-muted-foreground">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function CheckboxRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="block font-medium">{label}</span>
        <span className="block text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}

function RowDelete({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded p-1 text-muted-foreground hover:text-destructive disabled:opacity-40"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}
